import React, { useEffect, useState } from 'react';
import { X, Download, Smartphone } from 'lucide-react';

/**
 * Install-app prompt component.
 * - Captures the `beforeinstallprompt` event on Chromium-based browsers
 *   and shows a small bottom banner with an "Install" button.
 * - On iOS Safari (which doesn't expose the event), shows a one-time hint
 *   guiding users to Share → Add to Home Screen.
 * - Dismissals persist for 7 days via localStorage.
 */
export function InstallAppPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    const dismissedAt = parseInt(localStorage.getItem('install-prompt-dismissed-at') || '0', 10);
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    if (dismissedAt && Date.now() - dismissedAt < sevenDaysMs) return;

    // Already installed?
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) return;

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isStandalone = window.navigator.standalone;
    if (isIOS && !isStandalone) {
      // Delay to avoid clobbering first paint
      const t = setTimeout(() => setIosHint(true), 4000);
      return () => clearTimeout(t);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const onInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShow(false);
  };

  const onDismiss = () => {
    localStorage.setItem('install-prompt-dismissed-at', String(Date.now()));
    setShow(false);
    setIosHint(false);
  };

  if (iosHint) {
    return (
      <div className="fixed bottom-20 md:bottom-6 left-3 right-3 md:left-auto md:right-6 md:max-w-sm z-[10000] animate-in slide-in-from-bottom-5 fade-in duration-300" data-testid="ios-install-hint">
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-[#DC2626]/10 rounded-xl flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-[#DC2626]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900 dark:text-white">Install SGI HRMS</p>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
                Tap <span className="inline-block px-1 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-[10px] font-mono">Share</span> then <strong>Add to Home Screen</strong> for the full app experience.
              </p>
            </div>
            <button onClick={onDismiss} className="flex-shrink-0 p-1 -m-1" data-testid="install-dismiss-btn" aria-label="Dismiss">
              <X className="w-4 h-4 text-slate-400" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!show) return null;

  return (
    <div className="fixed bottom-20 md:bottom-6 left-3 right-3 md:left-auto md:right-6 md:max-w-sm z-[10000] animate-in slide-in-from-bottom-5 fade-in duration-300" data-testid="install-prompt">
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl rounded-2xl p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 bg-[#DC2626]/10 rounded-xl flex items-center justify-center">
            <Download className="w-5 h-5 text-[#DC2626]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">Install SGI HRMS</p>
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
              Add to your home screen for faster access and a native-app feel.
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={onInstall}
                className="px-3 py-1.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-xs font-semibold rounded-md transition-colors"
                data-testid="install-prompt-install-btn"
              >
                Install
              </button>
              <button
                onClick={onDismiss}
                className="px-3 py-1.5 text-slate-600 dark:text-slate-300 text-xs font-medium rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                data-testid="install-prompt-later-btn"
              >
                Maybe later
              </button>
            </div>
          </div>
          <button onClick={onDismiss} className="flex-shrink-0 p-1 -m-1" data-testid="install-prompt-close-btn" aria-label="Dismiss">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>
      </div>
    </div>
  );
}
